export type MotifStrategy = 'classic' | 'invert' | 'retrograde' | 'expand' | 'transpose';

export class MotifBrain {
    private motifBuffer: number[] = [];
    private motifState: 'recording' | 'replaying' = 'recording';
    private motifReplayIndex: number = 0;
    private currentStrategy: MotifStrategy = 'classic';

    constructor() {
        this.reset();
    }

    public reset() {
        this.motifBuffer = [];
        this.motifState = 'recording';
        this.motifReplayIndex = 0;
        this.currentStrategy = 'classic';
    }

    public setStrategy(strategy: MotifStrategy) {
        this.currentStrategy = strategy;
    }

    public isReplaying(): boolean {
        return this.motifState === 'replaying';
    }

    public getReplayStep(currentSection: 'Verse' | 'Chorus' | 'Bridge'): number | null {
        if (this.motifState !== 'replaying') return null;

        let indexToRead = this.motifReplayIndex;

        // TRANSFORM 1: RETROGRADE (Play backwards)
        if (this.currentStrategy === 'retrograde') {
            indexToRead = this.motifBuffer.length - 1 - this.motifReplayIndex;
        }

        // Safety check
        if (indexToRead < 0 || indexToRead >= this.motifBuffer.length) {
            this.finishReplay();
            return null;
        }

        const storedMove = this.motifBuffer[indexToRead];
        let finalMove = storedMove;

        // TRANSFORM 2: INVERSION ( Flip intervals )
        // Logic: specific strategy OR random chance in Bridge
        const forceInvert = this.currentStrategy === 'invert';
        const chanceInvert = this.currentStrategy === 'classic' && (currentSection === 'Bridge' || Math.random() < 0.2);

        if (forceInvert || chanceInvert) {
            finalMove = -storedMove;
        }

        // TRANSFORM 3: EXPANSION ( Wider intervals )
        if (this.currentStrategy === 'expand') {
            // Make jumps larger, but keep small steps small
            if (Math.abs(finalMove) > 1) {
                finalMove = Math.round(finalMove * 1.5);
            }
        }

        // TRANSFORM 4: TRANSPOSITION ( Shift up/down )
        // Not a direct interval change, but we need to signal MelodyGenerator?
        // Actually, MelodyGenerator handles the starting note. 
        // MotifBrain just gives the relative Step.
        // Wait, if we want to transpose the *shape*, the steps remain the same!
        // Transposition happens by starting on a different root index in MelodyGenerator.
        // BUT, we can simulate "Modal Transposition" by adding a constant offset to the steps?
        // No, stepping +1 is moving up the scale. 
        // If we want to play the motif "higher", we just start higher. 
        // The steps (shape) are identical. 
        // So 'transpose' strategy in MotifBrain might actually mean:
        // "Alter the shape slightly to fit a new harmony?" -> That's automatic via scale.
        // Let's make 'transpose' strictly about altering the *intervallic* content if needed,
        // OR we interpret 'transpose' as "Sequence" (e.g. +1 to every step value? No that changes the shape).

        // Let's implement 'transpose' as Octave Displacement for variety
        if (this.currentStrategy === 'transpose') {
            // 20% chance to jump an octave on a step
            if (Math.random() < 0.2) {
                finalMove += (finalMove > 0 ? 7 : -7);
            }
        }

        this.motifReplayIndex++;
        if (this.motifReplayIndex >= this.motifBuffer.length) {
            this.finishReplay();
        }

        return finalMove;
    }

    private finishReplay() {
        this.motifState = 'recording';
        this.motifBuffer = [];
        this.motifReplayIndex = 0;
    }

    public recordStep(signedStep: number) {
        if (this.motifState !== 'recording') return;

        this.motifBuffer.push(signedStep);

        // Logic to switch to Replay
        // "Musician Brain": Don't just replay randomly. 
        // Replay if we have a solid phrase (4-8 notes)
        if (this.motifBuffer.length >= 4) {
            // Higher chance to replay if buffer is getting long
            const probability = 0.2 + (this.motifBuffer.length * 0.05);

            if (Math.random() < probability) {
                this.motifState = 'replaying';
                this.motifReplayIndex = 0;
                // console.log(`[MotifBrain] Replaying motif (Len: ${this.motifBuffer.length}, Strat: ${this.currentStrategy})`);
            } else if (this.motifBuffer.length > 12) {
                // Clear old buffer if too long
                this.motifBuffer = [];
            }
        }
    }
}
